/* eslint-disable react/no-unstable-nested-components */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	type ColumnDef,
	type ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';

import ConfirmModal from '@/components/ConfirmModal/ConfirmModal';
import { DataTableColumnHeader } from '@/components/DataTable/DataTableColumnHeader';
import { DataTablePagination } from '@/components/DataTable/DataTablePagination';
import TableFilters from '@/components/TableFilters/TableFilters';
import Tag from '@/components/Tag/Tag';
import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { deleteTagFrag } from '@/hooks/mutations/tagMutations';
import { useGetDisciplines } from '@/hooks/queries/useDisciplines';
import { useGetTags } from '@/hooks/queries/useTags';
import { useUserRole } from '@/hooks/useAuth';
import { EditIcon, TrashCanIcon } from '@/icons/index';
import type { Tag as TagType } from '@/lib/types';

import TagListModal from './TagListModal';

const TagList = () => {
	const [modalActive, setModalActive] = useState(false);
	const [modalType, setModalType] = useState(false);
	const [currentTag, setCurrentTag] = useState<CourseTag>({
		tag_color: 'hsl(281, 50%, 50%)',
		tag_name: '',
		tag_id: '',
		tag_definition: '',
		tag_discipline: '',
	});
	const [isConfirming, setIsConfirming] = useState(false);
	const [allTags, setAllTags] = useState<TagType[] | undefined>();
	const { data: tags, isFetching, isLoading } = useGetTags();
	const { data: disciplines, isLoading: isDisciplineLoading } = useGetDisciplines();
	const { data: userRoleData } = useUserRole();
	const queryClient = useQueryClient();

	const triggerModal = useCallback(() => {
		setModalActive(!modalActive);
	}, [modalActive]);

	const [filteredData, setFilteredData] = useState<TagTableData[]>([]);
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

	const handleSetData = useCallback((queryTagData: any) => {
		setFilteredData(queryTagData as TagTableData[]);
	}, []);

	const { mutate: deleteTagMutation } = useMutation({
		mutationFn: deleteTagFrag,
		onSuccess: () => {
			const schoolId = import.meta.env.VITE_SCHOOL_ID;
			queryClient.invalidateQueries({
				queryKey: ['get-all-tags', schoolId],
			});
			showSuccessToast('Tag Deleted');
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const removeTag = () => {
		deleteTagMutation({
			id: currentTag.tag_id,
			role: userRoleData?.user_role ?? '',
		});
	};

	const confirmDelete = useCallback((tag: CourseTag) => {
		setCurrentTag(tag);
		setIsConfirming(true);
	}, []);

	useEffect(() => {
		const abortController = new AbortController();
		setAllTags(tags);
		return () => {
			abortController.abort();
		};
	}, [tags]);

	type TagTableData = {
		idColumn: string;
		nameColumn: string;
		colorColumn: string;
		actionsColumn: TagType;
	};

	const queryTagData = useMemo(
		() =>
			allTags?.map((tag) => ({
				idColumn: tag.tag_id,
				nameColumn: tag.tag_name,
				colorColumn: tag.tag_color,
				actionsColumn: tag,
			})),
		[allTags]
	);

	useEffect(() => {
		if (queryTagData) {
			setFilteredData(queryTagData as TagTableData[]);
		}
	}, [queryTagData]);

	const columns = useMemo<ColumnDef<TagTableData>[]>(
		() => [
			{
				accessorKey: 'nameColumn',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
				cell: ({ row }) => (
					<Tag
						key={row.original.idColumn}
						color={row.original.colorColumn}
						text={row.original.nameColumn}
					/>
				),
			},
			{
				accessorKey: 'colorColumn',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Color" className="text-center" />
				),
				cell: ({ row }) => (
					<div className="flex justify-center">
						<div
							className="h-8 w-8 rounded-full"
							style={{ backgroundColor: row.getValue('colorColumn') }}
						/>
					</div>
				),
				enableSorting: false,
			},
			{
				id: 'actions',
				enableHiding: false,
				cell: ({ row }) => {
					const updateTagModal = () => {
						triggerModal();
						setModalType(true);
						setCurrentTag(row.original.actionsColumn as CourseTag);
						setModalActive(!modalActive);
					};

					return (
						<div className="flex items-center justify-center [&>button:not(:last-child)]:mr-4">
							<EditIcon passedEvent={updateTagModal} />
							<TrashCanIcon
								passedEvent={() => confirmDelete(row.original.actionsColumn as CourseTag)}
							/>
						</div>
					);
				},
			},
		],
		[triggerModal, modalActive, confirmDelete]
	);

	const table = useReactTable({
		data: filteredData,
		columns,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
		},
		initialState: {
			pagination: {
				pageSize: 10,
			},
		},
	});

	return (
		<>
			{modalActive && (
				<TagListModal
					modalActive={modalActive}
					triggerModal={triggerModal}
					isEditing={modalType}
					tagData={currentTag}
					tagList={queryTagData ?? []}
					disciplines={disciplines}
				/>
			)}
			{isConfirming && (
				<ConfirmModal
					modalActive={isConfirming}
					onConfirm={removeTag}
					onCancel={() => setIsConfirming(false)}
					triggerModal={() => setIsConfirming(false)}
					message="Are you sure you want to delete this tag?"
					selectedData={currentTag.tag_name}
				/>
			)}
			<div className="flex items-center justify-between">
				<Button
					className="self-start mr-auto"
					size="small"
					onClick={() => {
						triggerModal();
						setModalType(false);
					}}
				>
					Add New Tag
				</Button>
				{queryTagData && (
					<TableFilters
						data={queryTagData}
						handleSetData={handleSetData}
						searchColumn={['nameColumn']}
						filteredColumn="nameColumn"
						resultQty={filteredData.length}
						dropdown={false}
					/>
				)}
			</div>
			{isLoading ||
				isDisciplineLoading ||
				(isFetching && (
					<div className="flex flex-col pb-5 h-full">
						<Skeleton className="w-full mb-3 h-24" />
						<Skeleton className="w-full mb-3 h-24" />
						<Skeleton className="w-full mb-3 h-24" />
						<Skeleton className="w-full mb-3 h-24" />
					</div>
				))}
			{!isFetching && (
				<div className="flex-1 flex flex-col min-h-0">
					<div
						className="flex-1 overflow-auto rounded-md"
						style={{ maxHeight: 'calc(100vh - 300px)' }}
					>
						<Table className="text-md typo-body-2">
							<TableHeader className="sticky top-0 bg-background z-10">
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => {
											return (
												<TableHead key={header.id}>
													{header.isPlaceholder
														? null
														: flexRender(header.column.columnDef.header, header.getContext())}
												</TableHead>
											);
										})}
									</TableRow>
								))}
							</TableHeader>
							<TableBody>
								{table.getRowModel().rows?.length ? (
									table.getRowModel().rows.map((row) => (
										<TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
											{row.getVisibleCells().map((cell) => (
												<TableCell key={cell.id}>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</TableCell>
											))}
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={columns.length} className="h-24 text-center">
											No results.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
					<div className="mt-4 shrink-0 pb-4 border-t border-gray-200 pt-4">
						<DataTablePagination table={table} />
					</div>
				</div>
			)}
		</>
	);
};

export default TagList;
